/**
 * RegenerationPoller (Phase 44.7).
 *
 * Modal that surfaces the state of an in-flight bootstrap regeneration
 * (fired from ResearchPanel). Reads `regenerationJob` + `regenerationStatus`
 * from the setupStore — the store already polls every 3s and refreshes
 * setup data on SUCCEEDED, so this component is purely presentational.
 *
 * Rendered at the TenantSetupPage level so a regeneration in-flight on
 * the catalogue doesn't vanish when the user navigates to /setup/profile.
 *
 * The modal has:
 *   - a heading that reads the mode (profile / catalogue)
 *   - a progress line showing the SFN execution status
 *   - Dismiss on terminal success (closes + optional toast)
 *   - Retry + Dismiss on terminal failure
 *   - "Still running? Keep editing — the changes will appear here"
 *     on the poll-timeout branch
 */

import { useMemo, useRef, useState } from "react";
import { useSetupStore } from "../../../stores/setupStore.js";

const TERMINAL = new Set(["SUCCEEDED", "FAILED", "TIMED_OUT", "ABORTED"]);
const MODE_LABELS = { profile: "TenantProfile", catalogue: "ProductCatalogue" };

export default function RegenerationPoller() {
  const regenerationJob = useSetupStore((s) => s.regenerationJob);
  const regenerationStatus = useSetupStore((s) => s.regenerationStatus);
  const regenerationError = useSetupStore((s) => s.regenerationError);
  const regenerate = useSetupStore((s) => s.regenerate);
  const [dismissed, setDismissed] = useState(false);

  // Reset dismiss when a fresh job arrives (new execution ARN) without
  // firing a side-effect from within useEffect.
  const arn = regenerationJob?.executionArn;
  const lastArnRef = useRef(null);
  if (arn !== lastArnRef.current) {
    lastArnRef.current = arn;
    // Derived state — OK to set during render, React will re-render once.
    if (dismissed) setDismissed(false);
  }

  const status = regenerationStatus?.status;
  const isTerminal = status && (TERMINAL.has(status) || status === "TIMED_OUT_POLL");
  const isPollTimeout = status === "TIMED_OUT_POLL";
  const succeeded = status === "SUCCEEDED";
  const failed = status && !succeeded && isTerminal;

  const title = useMemo(() => {
    const label = MODE_LABELS[regenerationJob?.mode] || "content";
    if (succeeded) return `${label} regenerated`;
    if (failed && !isPollTimeout) return `${label} regeneration ${status.toLowerCase()}`;
    if (isPollTimeout) return `${label} regeneration still running`;
    return `Regenerating ${label}…`;
  }, [regenerationJob, succeeded, failed, isPollTimeout, status]);

  if (!regenerationJob || dismissed) return null;

  return (
    <div className="regen-modal-backdrop" role="dialog" aria-modal="true" aria-label={title}>
      <div className="regen-modal">
        <h3 className="regen-modal__title">{title}</h3>

        {!isTerminal && (
          <div className="regen-modal__body">
            <div className="regen-modal__spinner" aria-hidden="true" />
            <p className="regen-modal__muted">
              {regenerationJob?.mode === "catalogue"
                ? "Drafting a ProductCatalogue from your TenantProfile + prior research. This usually takes about 30 seconds."
                : "Running the research pipeline (homepage → product pages → Tavily queries → synthesis). This usually takes 2–5 minutes."}
            </p>
            {status && (
              <div className="regen-modal__status-line">
                Execution status:{" "}
                <code className="regen-modal__code">{status}</code>
              </div>
            )}
            <p className="regen-modal__hint">
              You can close this and keep editing — the {MODE_LABELS[regenerationJob?.mode] || "content"}{" "}
              will update in place when the run finishes.
            </p>
          </div>
        )}

        {succeeded && (
          <div className="regen-modal__body">
            <p>
              Your {MODE_LABELS[regenerationJob?.mode] || "content"} has been
              regenerated and refreshed.{" "}
              {regenerationJob?.mode === "catalogue"
                ? "Review the new pillars and capabilities below."
                : "Review the new profile below."}
            </p>
          </div>
        )}

        {failed && (
          <div className="regen-modal__body">
            <p className="regen-modal__error">
              {isPollTimeout
                ? "Regeneration is taking longer than expected. It may still complete — keep editing, and the content will update if it finishes. Otherwise, try Retry."
                : `Regeneration ended with status ${status}. Retry to try again, or dismiss to keep your current content.`}
            </p>
            {regenerationError && (
              <p className="regen-modal__muted">Error: {regenerationError}</p>
            )}
          </div>
        )}

        <div className="regen-modal__actions">
          {isTerminal && failed && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => regenerate(regenerationJob.mode)}
            >
              Retry
            </button>
          )}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setDismissed(true)}
          >
            {isTerminal ? "Close" : "Keep editing"}
          </button>
        </div>
      </div>
    </div>
  );
}
